import { useEffect, useState } from 'react';

import { Body, H2, Loader, PasswordTextField, PrimaryButton, SubBody } from '@rikstv/shared-components';

import config from '../../config';
import { Link } from '../../router/Link';
import { Modal } from '../modal/Modal';

import { usePinStore } from './usePinStore';

import styles from './PinPanel.module.css';

type PinInputContext = 'parentalSettings' | 'parentalControls' | 'transactionControl';

const titleMap: Record<PinInputContext, string> = {
  parentalSettings: 'Endring av foreldrekontroll',
  parentalControls: 'Foreldrekontroll aktivert',
  transactionControl: 'Tast in PIN-kode',
};

export const PinPanelModal = ({
  assetId,
  isOpen,
  onClose,
  context,
  ageRestriction,
  onAccessGranted,
}: {
  assetId?: string;
  isOpen: boolean;
  onClose: () => void;
  context: PinInputContext;
  ageRestriction?: number;
  onAccessGranted?: () => void;
}) => {
  const brand = config.application === 'RiksTV' ? 'rtv' : 'strm';
  const { validatedTitles, isPurchasePinActive, assetIsAgeRestricted, validatePin, isValidatingPin, pinMessage } =
    usePinStore();
  const [displayPinPanel, setDisplayPinPanel] = useState(true);
  const [pinInput, setPinInput] = useState<string>('');
  const title = titleMap[context];
  const body =
    context === 'parentalSettings'
      ? 'Bruk PIN-kode for å slå av eller endre på foreldrekontroll'
      : 'Tast PIN-kode for å låse opp';

  const isAgeRestricted = assetIsAgeRestricted(ageRestriction);
  const enableTransactionControl = context === 'transactionControl' && isPurchasePinActive;
  const forgotPinText =
    brand === 'rtv' ? (
      <SubBody className={styles.text}>
        Har du glemt koden eller vil endre pin? Gå til Pin og passord på{' '}
        <Link style={{ textDecoration: 'underline' }} to="https://minside.rikstv.no/pin-og-passord">
          Min profil
        </Link>
        .
      </SubBody>
    ) : (
      <div className={styles.forgotPinContainer}>
        <SubBody className={styles.text}>Har du glemt koden?</SubBody>{' '}
        <Link to={'/min-konto/innstillinger?tilbakestill-pin'}>Gjenopprett på Min konto </Link>
      </div>
    );

  useEffect(() => {
    //If title already has been validated make an early return
    if (assetId && validatedTitles.includes(assetId)) {
      onAccessGranted?.();
      onClose();
      setDisplayPinPanel(false);
    }

    //If relevant pin context is disabled make an early return
    if (!isAgeRestricted && !enableTransactionControl && context !== 'parentalSettings') {
      onAccessGranted?.();
      onClose();
      setDisplayPinPanel(false);
    }
  }, [
    isAgeRestricted,
    assetId,
    assetIsAgeRestricted,
    context,
    enableTransactionControl,
    onAccessGranted,
    onClose,
    validatedTitles,
  ]);

  if (!displayPinPanel) {
    return null;
  }

  //If isPurchasPinActive === undefined, pin settings hasn't loaded yet
  if (isPurchasePinActive == null) {
    return (
      <div className={styles.modalContainer}>
        <Loader delay={0} size="large" />
      </div>
    );
  }
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    setPinInput(e.currentTarget.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPinInput('');

    const validationResponse = await validatePin(pinInput, assetId);
    if (validationResponse && onAccessGranted) {
      onAccessGranted?.();
      onClose();
    }
  };

  return (
    <Modal center open={isOpen} onClose={() => onClose()}>
      <div className={styles.modalContainer}>
        <H2 className={styles.title}>{title}</H2>
        <Body className={styles.text}>{body}</Body>
        <form data-testid="pin-panel" onSubmit={e => handleSubmit(e)} className={styles.form}>
          <div className={styles.inputContainer}>
            <div className={styles.pinInput}>
              <PasswordTextField
                data-testid="pin-input"
                value={pinInput}
                onChange={e => handleChange(e)}
                name={'pin'}
                label={'4 siffer'}
                maxLength={4}
                inputMode="numeric"
                autoComplete="off"
                errorMessage={pinMessage?.type === 'error' ? pinMessage.message : undefined}
                disabled={isValidatingPin === true}
              />
            </div>
            <PrimaryButton
              data-testid="pin-input-button"
              className={styles.button}
              isLoading={isValidatingPin === true}
              type="submit"
              disabled={!pinInput || pinInput?.length < 4}>
              Bekreft
            </PrimaryButton>
          </div>
        </form>
        {forgotPinText}
      </div>
    </Modal>
  );
};
